import { UnixTimestamp } from '@module/assignment';
import { ClockCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { useEffect, useRef, useState } from 'react';
import { getSize } from '@lib/common';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var timerAccent = '#E91E63';
var timerSurface = '#fff0f3';
var timerBorder = '#ffd6e0';

var getEndTimeMs = function getEndTimeMs(endTimeUnix) {
  if (!endTimeUnix) {
    return null;
  }

  var parsed = parseInt(endTimeUnix, 10);

  if (Number.isNaN(parsed)) {
    return null;
  }

  return UnixTimestamp.isInSecondType(endTimeUnix) ? parsed * 1000 : parsed;
};

var getRemainingSeconds = function getRemainingSeconds(endTimeUnix) {
  var endMs = getEndTimeMs(endTimeUnix);

  if (endMs === null) {
    return 0;
  }

  return Math.max(0, Math.floor((endMs - Date.now()) / 1000));
};

var formatCountdown = function formatCountdown(remaining) {
  var hours = Math.floor(remaining / 3600);
  var minutes = Math.floor(remaining % 3600 / 60);
  var seconds = remaining % 60;
  return [hours, minutes, seconds].map(function (unit) {
    return String(unit).padStart(2, '0');
  }).join(':');
};

var Root = styled.div.withConfig({
  displayName: "assignment-count-down-timercomponent__Root",
  componentId: "sc-1fkkqc6-0"
})(["display:inline-flex;align-items:center;gap:6px;padding:4px 12px;border:1px solid ", ";border-radius:", ";background:", ";color:", ";font-weight:700;line-height:1.2;flex-shrink:0;.anticon{color:", ";}"], timerBorder, getSize('radiusSm'), timerSurface, timerAccent, timerAccent);
export var AssignmentCountDownTimer = function AssignmentCountDownTimer(_ref) {
  var endTimeUnix = _ref.endTimeUnix,
      onTimeout = _ref.onTimeout,
      _ref$labelBeforeTime = _ref.labelBeforeTime,
      labelBeforeTime = _ref$labelBeforeTime === void 0 ? '' : _ref$labelBeforeTime,
      _ref$labelAfterTime = _ref.labelAfterTime,
      labelAfterTime = _ref$labelAfterTime === void 0 ? '' : _ref$labelAfterTime;

  var _useState = useState(function () {
    return getRemainingSeconds(endTimeUnix);
  }),
      remaining = _useState[0],
      setRemaining = _useState[1];

  var onTimeoutRef = useRef(onTimeout);
  var hasFiredTimeoutRef = useRef(false);
  useEffect(function () {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);
  useEffect(function () {
    hasFiredTimeoutRef.current = false;
    var initialRemaining = getRemainingSeconds(endTimeUnix);
    setRemaining(initialRemaining); // Only notify when the deadline is crossed during this session (was open, now closed).
    // If remaining is already 0 on load (e.g. clock skew, format mismatch with isEnded()),
    // firing onTimeout would cause a reload loop.

    if (initialRemaining <= 0) {
      return undefined;
    }

    var endMs = getEndTimeMs(endTimeUnix);

    if (endMs === null) {
      return undefined;
    }

    var fireTimeout = function fireTimeout() {
      var _onTimeoutRef$current;

      if (hasFiredTimeoutRef.current) {
        return;
      }

      hasFiredTimeoutRef.current = true;
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
      setRemaining(0);
      (_onTimeoutRef$current = onTimeoutRef.current) === null || _onTimeoutRef$current === void 0 ? void 0 : _onTimeoutRef$current.call(onTimeoutRef);
    };

    var intervalId = window.setInterval(function () {
      var nextRemaining = getRemainingSeconds(endTimeUnix);
      setRemaining(nextRemaining);

      if (nextRemaining <= 0) {
        fireTimeout();
      }
    }, 1000); // Exact deadline timer so we still fire if the 1s interval is throttled/delayed.

    var timeoutId = window.setTimeout(fireTimeout, Math.max(0, endMs - Date.now()));
    return function () {
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
    };
  }, [endTimeUnix]);

  if (!endTimeUnix) {
    return null;
  }

  var prefix = labelBeforeTime ? "".concat(labelBeforeTime.trim(), " ") : '';
  var suffix = labelAfterTime ? " ".concat(labelAfterTime.trim()) : '';
  return /*#__PURE__*/_jsxs(Root, {
    children: [/*#__PURE__*/_jsx(ClockCircleOutlined, {}), /*#__PURE__*/_jsxs("span", {
      children: [prefix, formatCountdown(remaining), suffix]
    })]
  });
};